import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/lang";

export default function PaymentSuccess() {
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }

    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const data = await api.getCheckoutStatus(sessionId);
        setPaymentData(data);
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setStatus("timeout");
        }
      } catch {
        setStatus("error");
      }
    };

    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#05050A] flex items-center justify-center px-6" data-testid="payment-success-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card rounded-3xl p-10 text-center max-w-md w-full">
        {status === "checking" && (
          <>
            <div className="w-12 h-12 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-2">{t("pay_verifying")}</h2>
            <p className="text-[#A0A0AB] text-sm">{t("pay_wait")}</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle className="w-16 h-16 text-[#00F0FF] mx-auto mb-6" />
            <h2 className="font-['Unbounded'] text-2xl font-bold text-white mb-2">{t("pay_success")}</h2>
            <p className="text-[#A0A0AB] text-sm mb-2">{t("pay_thanks")}</p>
            {paymentData?.amount_total && (
              <p className="text-white font-bold text-lg mb-6">${(paymentData.amount_total / 100).toFixed(2)} USD</p>
            )}
            <div className="flex gap-3 justify-center flex-wrap">
              <Link to="/marketplace" className="neon-btn text-sm py-2 px-6">{t("pay_browse")}</Link>
              <Link to="/" className="neon-btn neon-btn-pink text-sm py-2 px-6">{t("pay_home")}</Link>
            </div>
          </>
        )}
        {(status === "error" || status === "timeout" || status === "expired") && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
              <span className="text-red-400 text-2xl">!</span>
            </div>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-2">
              {status === "expired" ? t("pay_expired") : t("pay_unknown")}
            </h2>
            <p className="text-[#A0A0AB] text-sm mb-6">
              {status === "expired" ? t("pay_expired_text") : t("pay_unknown_text")}
            </p>
            <Link to="/" className="neon-btn text-sm py-2 px-6">{t("pay_home")}</Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
